import { createContext, useState, useEffect, useContext } from 'react'
import { useRouter } from 'next/router'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { trackEvent } from '../analytics'
import { pathNameWithPageTitle, CREAT_JOB_STATUS, FILTER_ENDPOINT } from '../constants/all'
import { getMethod, seekersAnalayticOptions, structureIds } from '../utils/functions'
import { APIGet } from '../utils/ApiClient'
import Cookies from 'universal-cookie'

import { SharedContext } from './SharedContext'
import { Media } from '../commonComponents/Media'

import classNames from 'classnames'

import dynamic from 'next/dynamic'

const Header = dynamic(() => import('../components/Header').then((mod) => mod.Header), {
  ssr: false
})

const SkeletonsHandler = dynamic(
  () => import('../components/SkeletonsHandler').then((mod) => mod.SkeletonsHandler),
  {
    ssr: false
  }
)

const AppLinkSendModal = dynamic(
  () => import('../components/AppLinkSendModal').then((mod) => mod.AppLinkSendModal),
  {
    ssr: false
  }
)

const Toast = dynamic(() => import('../components/Toast').then((mod) => mod.Toast), {
  ssr: false
})

const LoginOrSignUp = dynamic(
  () => import('../components/LoginOrSignUp').then((mod) => mod.LoginOrSignUp),
  {
    ssr: false
  }
)

const HamburgerMenu = dynamic(
  () => import('../components/HamburgerMenu').then((mod) => mod.HamburgerMenu),
  {
    ssr: false
  }
)

export const SeekerContext = createContext()

export const SeekerContextProvider = ({ children }) => {
  // ----------- STATES (useState) -----------
  const [showCopyToast, setShowCopyToast] = useState(false)
  const [appLinkModal, setAppLinkModal] = useState({ isOpen: false, jobDetails: {} })
  const [filterTitleIds, setFilterTitleIds] = useState({})
  const [isLoginOpen, setLogInOpen] = useState(false)

  // ----------- STATES (useLocalStorage) -----------
  const [filterNameWFH, setFilterNameWFH] = useLocalStorage('filterName', '')
  const [isclickedHRCTA, setClickedHRCTA] = useLocalStorage('isclickedHRCTA', false)
  const [pageTitles, setPageTitles] = useLocalStorage('pageTitles', pathNameWithPageTitle)
  const [body, setBody] = useLocalStorage('body', { apiBody: {}, selectedOptions: {} })
  const [showSponsoredJobs, setShowSponsoredJobs] = useLocalStorage('showSponsoredJobs', false)
  const [from, setFrom] = useLocalStorage('from', '')
  const [isJobProfileCreated, setIsJobProfileCreated] = useLocalStorage(
    'isJobProfileCreated',
    false
  )
  const [currentPage, setCurrentPage] = useLocalStorage('currentPage', 0)
  const [isComponentOpen, setComponentOpen] = useState(false)

  const {
    loginDetails,
    utmParams,
    isMenuClicked,
    location,
    setMenuClicked,
    setScrollBar,
    showToast,
    skeletonloader
  } = useContext(SharedContext)

  const cookie = new Cookies()
  const router = useRouter()
  const pathname = router?.pathname?.split('/')?.[1]
  const isLocationPage =
    router?.asPath?.split('/')?.includes('location') ||
    router?.asPath?.split('/')?.includes('jobs-near-me') ||
    router?.asPath?.split('/')?.includes('jobs-near-me?clicked=create_profile')
  const isSeekerProfile = router?.asPath?.includes('seeker_profile')

  const checkProfileStatus = async () => {
    if (loginDetails?.isLogIn) {
      const user = cookie.get('user')
      if (user) {
        const res = await getMethod({
          url: process.env.NEXT_PUBLIC_BASE_URL_ADS + CREAT_JOB_STATUS + `${user}/status/`,
          headers: { 'App-Type': 'web' }
        })

        if (res?.status === 2) setIsJobProfileCreated(true)
        else setIsJobProfileCreated(false)
      }
    }
  }

  const findFilterTitleIds = async () => {
    const data = await APIGet(FILTER_ENDPOINT, { 'App-Type': 'web' })
    const result = structureIds(data)
    setFilterTitleIds({ ...result })
  }

  const closeSeekerComponents = () => {
    if (!isLoginOpen) {
      setMenuClicked(false)
    }
    setLogInOpen(false)
    setShowSponsoredJobs(false)
    setScrollBar(true)
  }

  const popSeekerPage = () => {
    closeSeekerComponents()
    setPageTitles((prev) => {
      if (prev?.[pathname]?.length > 1) {
        prev?.[pathname]?.pop()
      }
      return { ...prev }
    })
    return false
  }

  const backNavigation = async () => {
    router.back()
  }

  const locationHandler = () => {
    trackEvent({
      name: 'location_dropdown_clicked',
      searchParams: {
        ...utmParams
      },
      options: seekersAnalayticOptions(window, loginDetails, location, 'tap', 'job_seeker_home'),
      isSeeker: true
    })
  }

  const loginClose = () => {
    setLogInOpen(false)
  }

  useEffect(() => {
    checkProfileStatus()
  }, [loginDetails?.isLogIn])

  useEffect(() => {
    if (isLoginOpen)
      trackEvent({
        name: 'seeker_login_screen_viewed',
        searchParams: {
          ...utmParams
        },
        options: seekersAnalayticOptions(
          window,
          loginDetails,
          location,
          'impression',
          'seeker_login_page'
        ),
        isSeeker: true
      })
  }, [isLoginOpen])

  useEffect(() => {
    if (isMenuClicked)
      trackEvent({
        name: 'clicker_hamburger',
        searchParams: {
          ...utmParams
        },
        options: seekersAnalayticOptions(window, loginDetails, location, 'tap', 'seeker_home_page'),
        isSeeker: true
      })
  }, [isMenuClicked])

  useEffect(() => {
    if (!loginDetails?.isLogIn) {
      setIsJobProfileCreated(false)
      cookie.set('isLogin', false, { path: '/' })
    } else {
      cookie.set('isLogin', true, { path: '/' })
    }
  }, [loginDetails?.isLogIn])

  useEffect(() => {
    if (Object?.keys(filterTitleIds)?.length <= 0) findFilterTitleIds()
  }, [router])

  useEffect(() => {
    closeSeekerComponents()
  }, [])

  useEffect(() => {
    if (isLoginOpen || showSponsoredJobs || isMenuClicked) {
      setComponentOpen(true)
    } else if (isComponentOpen) {
      setComponentOpen(false)
    }
  }, [isLoginOpen, showSponsoredJobs, isMenuClicked])

  useEffect(() => {
    const handleStart = async (url) => {
      const prvData = (await JSON.parse(localStorage.getItem('historyStack') || '[]')) || []
      if (prvData?.data?.[prvData?.data?.length - 2]?.as === url) {
        if (isComponentOpen && !(router.pathname === '/seekerHomePage')) {
          history.forward()
          popSeekerPage()
        }
      }
    }

    const popState = async (event) => {
      popSeekerPage()
    }

    router.events.on('routeChangeStart', handleStart)
    addEventListener('popstate', popState)
    return () => {
      router.events.off('routeChangeStart', handleStart)
      removeEventListener('popstate', popState)
    }
  }, [router, isComponentOpen, isLoginOpen])

  return (
    <SeekerContext.Provider
      value={{
        // U states
        showCopyToast,
        setShowCopyToast,
        appLinkModal,
        setAppLinkModal,
        filterTitleIds,
        setFilterTitleIds,
        isLoginOpen,
        setLogInOpen,

        // L states
        filterNameWFH,
        setFilterNameWFH,
        isclickedHRCTA,
        setClickedHRCTA,
        pageTitles,
        setPageTitles,
        body,
        setBody,
        showSponsoredJobs,
        setShowSponsoredJobs,
        from,
        setFrom,
        isJobProfileCreated,
        setIsJobProfileCreated,
        currentPage,
        setCurrentPage
      }}
    >
      {isLocationPage ? (
        <div className="sticky top-0 z-20">
          <Media at="sm">
            <Header
              {...{
                setMenuClicked,
                isMenuClicked,
                locationHandler,
                backNavigation,
                isLocationPage
              }}
            />
          </Media>
        </div>
      ) : (
        <>
          {!isSeekerProfile && (
            <div className="sticky top-0 z-50 bg-white">
              <Media at="sm">
                {!showSponsoredJobs && (
                  <Header
                    {...{
                      setMenuClicked,
                      isMenuClicked,
                      locationHandler,
                      backNavigation
                    }}
                  />
                )}
              </Media>
              <Media greaterThan="sm">
                <Header
                  {...{
                    setMenuClicked,
                    isMenuClicked,
                    locationHandler,
                    backNavigation
                  }}
                />
              </Media>
            </div>
          )}
        </>
      )}
      {skeletonloader?.isVisible && <SkeletonsHandler skeletonName={skeletonloader?.pathName} />}
      <Media at="sm">
        {!skeletonloader?.isVisible &&
          (isMenuClicked ? (
            <HamburgerMenu
              {...{
                setMenuClicked,
                setLogInOpen,
                isMenuClicked,
                isJobProfileCreated,
                setIsJobProfileCreated,
                setBody
              }}
            />
          ) : (
            children
          ))}
      </Media>
      <Media greaterThan="sm">
        {isMenuClicked && (
          <div className={classNames('fixed z-50 h-full w-full')} onClick={closeSeekerComponents} />
        )}
        {!skeletonloader?.isVisible && children}
      </Media>
      <AppLinkSendModal
        isOpen={appLinkModal?.isOpen}
        onClose={() => {
          setAppLinkModal({ isOpen: false, jobDetails: {} })
        }}
        {...{ jobDetails: appLinkModal?.jobDetails }}
      />
      {showToast?.isVisible && (
        <Toast
          {...{
            toastDetails: showToast?.details
          }}
        />
      )}
      <LoginOrSignUp {...{ isOpen: isLoginOpen, onClose: loginClose }} />
    </SeekerContext.Provider>
  )
}
